import { useCallback, useEffect, useRef, useSyncExternalStore, type ReactNode } from "react";
import styled from "styled-components";

import { ContentSwitcher, type ContentSwitcherOption } from "../ContentSwitcher";
import { IconButton, TooltipIconButton } from "../IconButton";
import {
  MultiSelectDropdown,
  type MultiSelectDropdownOption,
} from "../dropdown/MultiSelectDropdown";
import {
  SingleSelectDropdown,
  type SingleSelectDropdownOption,
} from "../dropdown/SingleSelectDropdown";
import {
  FilterController,
  type FilterGroup,
  type FilterSelectionChangeHandler,
} from "./FilterController";

export type FilterProps = {
  /** facet groups와 선택 상태를 단독 소유하는 외부 스토어. */
  controller: FilterController;
  /** 선택 변경 추가 구독. controller 생성자 콜백과 함께 통지된다. */
  onSelectionChange?: FilterSelectionChangeHandler;
  disabled?: boolean;
  /** shell의 aria-label. */
  label?: string;
  /** 그룹이 0개일 때 문구. */
  emptyMessage?: string;
  /** clear-all 버튼 텍스트. `clearIcon`이 있으면 아이콘 버튼의 접근성 이름(aria-label)으로 쓰인다. */
  clearLabel?: string;
  /**
   * clear-all을 아이콘 버튼으로 렌더할 때 쓸 글리프(icon-only). 주면 텍스트 라벨 대신 이 아이콘을
   * 표시하고 `clearLabel`을 접근성 이름으로 유지한다. 없으면(기본) `clearLabel` 텍스트 버튼.
   */
  clearIcon?: ReactNode;
  className?: string;
};

const defaultLabel = "Filters";
const defaultEmptyMessage = "No filters.";
const defaultClearLabel = "Clear filters";

const Shell = styled.div`
  display: flex;
  min-width: 0;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
`;

const EmptyMessage = styled.span`
  color: ${({ theme }) => theme.color.text.muted};
  font-size: ${({ theme }) => theme.typography.size.body};
`;

/**
 * facet 선택 바. `FilterGroup[]`을 그룹마다 다중선택 드롭다운(`display:"dropdown"`), 단일선택
 * 스위처(`display:"switcher"`), 단일선택 컴팩트 드롭다운(`display:"select"`)으로 렌더하고, 선택이
 * 있으면 clear-all을 노출한다.
 *
 * 상태와 groups는 필수 `controller`가 단독 소유하고, 이 컴포넌트는 스냅샷을 구독해 렌더한다.
 * 검색창·매칭모드 토글 등은 본 컴포넌트의 책임이 아니다 — 형제 컨트롤로 소비처가 조립한다.
 * (RFC-filter-search-primitives)
 */
export function Filter({
  controller,
  onSelectionChange,
  disabled = false,
  label = defaultLabel,
  emptyMessage = defaultEmptyMessage,
  clearLabel = defaultClearLabel,
  clearIcon,
  className,
}: FilterProps) {
  // onSelectionChange를 controller에 추가 구독하되, 첫 렌더 클로저에 고정되지 않도록 최신
  // prop을 ref로 읽는다(부모가 새 핸들러를 넘겨도 최신 콜백을 호출).
  const onSelectionChangeRef = useRef(onSelectionChange);
  useEffect(() => {
    onSelectionChangeRef.current = onSelectionChange;
  }, [onSelectionChange]);

  // onSelectionChange prop을 controller의 추가 selection 구독으로 연결한다.
  // controller가 생성자 콜백을 가져도 별개로 함께 통지된다.
  useEffect(
    () =>
      controller.subscribeSelectionChange((selection, changeSnapshot) =>
        onSelectionChangeRef.current?.(selection, changeSnapshot),
      ),
    [controller],
  );

  const snapshot = useSyncExternalStore(
    controller.subscribe,
    controller.getSnapshot,
    controller.getSnapshot,
  );
  const handleClearAll = useCallback(() => {
    controller.clearAll();
  }, [controller]);

  return (
    <Shell className={className} aria-label={label}>
      {snapshot.groups.length === 0 ? (
        <EmptyMessage>{emptyMessage}</EmptyMessage>
      ) : (
        snapshot.groups.map((group) =>
          group.display === "switcher" ? (
            <FilterSwitcherGroup
              key={group.value}
              group={group}
              disabled={disabled}
              selectedValues={snapshot.selection[group.value] ?? []}
              controller={controller}
            />
          ) : group.display === "select" ? (
            <FilterSelectGroup
              key={group.value}
              group={group}
              disabled={disabled}
              selectedValues={snapshot.selection[group.value] ?? []}
              controller={controller}
            />
          ) : (
            <FilterDropdownGroup
              key={group.value}
              group={group}
              disabled={disabled}
              selectedValues={snapshot.selection[group.value] ?? []}
              controller={controller}
            />
          ),
        )
      )}

      {snapshot.hasSelection ? (
        clearIcon != null ? (
          // icon-only — hover 시 clearLabel을 tooltip으로 노출하고, 그 텍스트가 접근성 이름도 겸한다
          // (TooltipIconButton이 string tooltip을 aria-label로 승격 — 이름/설명 중복 없음).
          <TooltipIconButton
            tooltip={clearLabel}
            size="sm"
            variant="ghost"
            disabled={disabled}
            onClick={handleClearAll}
          >
            {clearIcon}
          </TooltipIconButton>
        ) : (
          <IconButton
            text={clearLabel}
            size="sm"
            variant="ghost"
            disabled={disabled}
            onClick={handleClearAll}
          />
        )
      ) : null}
    </Shell>
  );
}

type FilterGroupViewProps = {
  group: FilterGroup;
  selectedValues: readonly string[];
  disabled: boolean;
  controller: FilterController;
};

function FilterDropdownGroup({
  group,
  selectedValues,
  disabled,
  controller,
}: FilterGroupViewProps) {
  const handleSelectionChange = useCallback(
    (nextSelectedValues: readonly string[]) => {
      controller.setGroupSelection(group.value, nextSelectedValues);
    },
    [controller, group.value],
  );

  return (
    <MultiSelectDropdown
      size="sm"
      mode="facet"
      label={group.label}
      options={group.filters as readonly MultiSelectDropdownOption[]}
      selectedValues={selectedValues}
      disabled={disabled || group.disabled}
      closeOnSelect={false}
      onSelectionChange={handleSelectionChange}
    />
  );
}

// "All"(선택 해제)의 내부 sentinel. 빈 문자열이면 value:""인 실제 필터와 충돌하므로(FilterOption.value는
// string이라 ""도 유효한 실값) 충돌 불가한 값을 쓴다. 컨트롤러 selection엔 절대 들어가지 않는다 — 고르면
// group selection을 비운다. switcher/select 공용.
const ALL_SENTINEL = "__c6_filter_all__";

// 단일선택 facet을 ContentSwitcher로 렌더한다. 맨 앞 "All" 세그먼트가 선택 해제 — ContentSwitcher는 항상
// 한 세그먼트가 활성이라 빈 선택을 "All"로 표현하고, 선택값이 옵션에 없으면 All로 graceful.
function FilterSwitcherGroup({
  group,
  selectedValues,
  disabled,
  controller,
}: FilterGroupViewProps) {
  const options: ContentSwitcherOption[] = [
    { value: ALL_SENTINEL, label: group.allLabel ?? "All" },
    ...group.filters.map((filter) => ({
      value: filter.value,
      label: filter.label,
      disabled: filter.disabled,
    })),
  ];
  const selected = selectedValues[0] ?? ALL_SENTINEL;
  const value = options.some((option) => option.value === selected) ? selected : ALL_SENTINEL;

  const handleValueChange = useCallback(
    (nextValue: string) => {
      controller.setGroupSelection(group.value, nextValue === ALL_SENTINEL ? [] : [nextValue]);
    },
    [controller, group.value],
  );

  return (
    <ContentSwitcher
      size="sm"
      label={group.label}
      options={options}
      value={value}
      disabled={disabled || group.disabled}
      onValueChange={handleValueChange}
    />
  );
}

// 단일선택 facet을 컴팩트 SingleSelectDropdown으로 렌더한다. 맨 앞 "All" 옵션이 선택 해제 상태로, 고르면
// group selection을 비운다. switcher와 같은 단일선택 의미지만 폭을 덜 먹어 좁은 컨테이너용. 빈 선택이면
// value=ALL_SENTINEL로 "All"이 메뉴에 체크되게 하고(단일선택은 항상 하나 선택), neutralValue로 그 값만
// 트리거 강조에서 빼 neutral로 "<label>: All"을 표시한다. 실제 필터가 걸리면 그 옵션 체크 + 트리거 accent.
function FilterSelectGroup({ group, selectedValues, disabled, controller }: FilterGroupViewProps) {
  const options: SingleSelectDropdownOption[] = [
    { value: ALL_SENTINEL, label: group.allLabel ?? "All" },
    ...group.filters.map((filter) => ({
      value: filter.value,
      label: filter.label,
      description: filter.description,
      count: filter.count,
      disabled: filter.disabled,
    })),
  ];
  // 첫 실선택값만 반영(단일선택 축). 빈 선택이거나 옵션에 없는 값이면 "All"이 선택된 것으로 둔다.
  const rawSelected = selectedValues[0];
  const value =
    rawSelected != null && group.filters.some((filter) => filter.value === rawSelected)
      ? rawSelected
      : ALL_SENTINEL;

  const handleValueChange = useCallback(
    (nextValue: string) => {
      controller.setGroupSelection(group.value, nextValue === ALL_SENTINEL ? [] : [nextValue]);
    },
    [controller, group.value],
  );

  return (
    <SingleSelectDropdown
      size="sm"
      mode="facet"
      label={group.label}
      options={options}
      value={value}
      neutralValue={ALL_SENTINEL}
      disabled={disabled || group.disabled}
      onValueChange={handleValueChange}
    />
  );
}
